import { CaretRight } from "@phosphor-icons/react";
import { useMemo, useState } from "react";

import MobileDetailsDrawer from "common/components/MobileDetailsDrawer";
import MobileDetailsDrawerItem from "common/components/MobileDetailsDrawer/MobileDetailsDrawerItem";
import MobileListTable from "common/components/MobileListTable";
import { Button } from "common/components/ui/Button";
import { DrawerHeader } from "common/components/ui/Drawer";

import { WEBHOOKS_ACTIONS } from "../../../constants";
import WebhookActions from "../WebhookActions";

import type { ColumnDef } from "@tanstack/react-table";
import type { Webhook } from "../../../types";

interface TableProps {
  webhooks: Webhook[];
}

export default function MobileList({ webhooks }: TableProps) {
  const [actionState, setActionState] = useState<{
    isOpen: boolean;
    selectedWebhookId: string;
  }>({ isOpen: false, selectedWebhookId: undefined });

  const { isOpen, selectedWebhookId } = actionState;

  const selectedWebhook =
    selectedWebhookId && webhooks.find(({ id }) => id === selectedWebhookId);

  const columns: ColumnDef<Webhook>[] = useMemo(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        cell: ({ row: { original } }) => (
          <span className="line-clamp-1 break-all text-sm-bold">
            {original.name}
          </span>
        ),
      },
      {
        id: "events",
        accessorKey: "events",
        header: "Events",
        cell: ({
          row: {
            original: { events },
          },
        }) => (
          <span className="line-clamp-1 text-button-xs">
            {events.length === 1
              ? events[0]
              : `${events[0]} (+ ${events.length - 1} more)`}
          </span>
        ),
      },
      {
        id: "actions",
        cell: () => (
          <Button intent="labelIcon" size="sm" variant="ghost-black">
            <CaretRight />
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <MobileListTable
        columns={columns}
        data={webhooks}
        onRowClick={({ id }) =>
          setActionState({ isOpen: true, selectedWebhookId: id })
        }
        isLoading={false}
      />

      {selectedWebhook && (
        <MobileDetailsDrawer
          isOpen={isOpen}
          setIsOpen={(isOpen) => setActionState({ ...actionState, isOpen })}
          editOptions={WEBHOOKS_ACTIONS}
          renderEditComponent={(editKey, onBack) => (
            <WebhookActions
              webhook={selectedWebhook}
              action={editKey}
              onClose={() => setActionState({ ...actionState, isOpen: false })}
              onBack={onBack}
            />
          )}
          editTitle="Webhook options"
        >
          <DrawerHeader title={selectedWebhook.name} />
          <MobileDetailsDrawerItem label="Link">
            {selectedWebhook.url}
          </MobileDetailsDrawerItem>
          <MobileDetailsDrawerItem label="Events">
            {selectedWebhook.events.map((eventType) => (
              <p className="mb-4 text-button-sm last:mb-0" key={eventType}>
                {eventType}
              </p>
            ))}
          </MobileDetailsDrawerItem>
        </MobileDetailsDrawer>
      )}
    </>
  );
}
